// A still map image (Mapbox Static Images API, same style as the live map) with our own overlay:
// the E2 modal's mini-map. Web Mercator on 512 px tiles, as Mapbox GL and the static API use.
import type { Position } from "../../shared/api";

const TILE = 512;
const MAX_ZOOM = 17;

const worldX = (lng: number, scale: number) => ((lng + 180) / 360) * scale;
const worldY = (lat: number, scale: number) => {
  const s = Math.sin((lat * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * scale;
};

/** Centre and zoom showing `points` in a `w` × `h` image with `pad` px around; and the projection. */
export function fitStatic(points: Position[], w: number, h: number, pad: number) {
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const center = {
    lat: (Math.min(...lats) + Math.max(...lats)) / 2,
    lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
  };
  const dx = worldX(Math.max(...lngs), TILE) - worldX(Math.min(...lngs), TILE);
  const dy = worldY(Math.min(...lats), TILE) - worldY(Math.max(...lats), TILE);
  const zx = dx > 0 ? Math.log2((w - 2 * pad) / dx) : MAX_ZOOM;
  const zy = dy > 0 ? Math.log2((h - 2 * pad) / dy) : MAX_ZOOM;
  const zoom = Math.min(MAX_ZOOM, Math.floor(Math.min(zx, zy) * 100) / 100);
  const scale = TILE * 2 ** zoom;
  const cx = worldX(center.lng, scale);
  const cy = worldY(center.lat, scale);
  return {
    center,
    zoom,
    project: (p: Position) => ({
      x: w / 2 + worldX(p.lng, scale) - cx,
      y: h / 2 + worldY(p.lat, scale) - cy,
    }),
  };
}

export function staticMapUrl(
  f: { center: Position; zoom: number },
  w: number,
  h: number,
  token: string,
): string {
  const at = `${f.center.lng.toFixed(6)},${f.center.lat.toFixed(6)},${f.zoom}`;
  const q = new URLSearchParams({ access_token: token, logo: "false", attribution: "false" });
  return `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/${at}/${w}x${h}@2x?${q}`;
}
